import { AimsCommonPopupEditorData, AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions, TableRowDelete, TableRowUpdate } from '@aims/common-components';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { timeStamp } from 'console';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { CacheItem, InventoryConfig, TenantConfig, TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { FacEquipment, InvAttachedToTypes, InvInventoryItem, WoWorkOrders } from 'src/models';
import { Cart, CartItem } from '../view-inventory.component';

@Component({
  selector: 'app-inventory-cart-popup',
  templateUrl: './inventory-cart-popup.component.html',
  styleUrls: ['./inventory-cart-popup.component.css']
})
export class InventoryCartPopupComponent implements OnInit {

  cartItemsHeader: AimsCommonTableHeader[] = [
    { key: 'editButton', label: 'Edit', type: 'editButton', filter: false },
    { key: 'inventoryItem.description', label: 'Description', type: 'string',width: 240, editable: false },
    { key: 'inventoryItem.partNo', label: 'Part #', type: 'string', width: 140, editable: false },
    { key: 'inventoryItem.unitOfIssueId', label: 'Unit of Issue', type: 'string', width: 60, editable: false  },
    { key: 'quantity', label: 'Qty Req', type: 'dynamic', validatorOrOpts: [Validators.min(0)], width: 100 },
    { key: 'instock', label: 'Qty Instock', type: 'dynamic', editable: false }
  ];
  cartItemsPermissions: AimsCommonTablePermissions = {
    create: false,
    update: true,
    delete: true,
    filter: false
  }

  //Used for aims-lookup
  equipmentLookupColumns: AimsCommonTableHeader[] = [
    { key: 'equipmentIdOld', label: 'Asset ID', type:AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'equipmentGroup', label: 'Asset Group', type:AimsCommonTableHeaderType.STRING, filter: true },
    { key: 'description', label: 'Description', type:AimsCommonTableHeaderType.STRING, filter: true },
  ];

  workOrderLookupColumns: AimsCommonTableHeader[] = [
    { key: 'workOrderNumber', label: 'WO #', type: AimsCommonTableHeaderType.STRING },
    { key: 'origin', label: 'Origin', type: AimsCommonTableHeaderType.STRING },
    { key: 'workOrderType', label: 'Type', type: AimsCommonTableHeaderType.STRING },
    { key: 'description', label: 'Description', type: AimsCommonTableHeaderType.STRING },
    { key: 'status', label: 'Status', type: AimsCommonTableHeaderType.STRING },
    { key: 'classification', label: 'Classification', type: AimsCommonTableHeaderType.STRING }
  ];

  scheduleItemLookupColumns:AimsCommonTableHeader[]  = [
    { key: 'travelerFullName', label: 'Traveler', filter: true, type:AimsCommonTableHeaderType.STRING },
    { key: 'scheduleItemType', label: 'Type', filter: true, type:AimsCommonTableHeaderType.STRING },
    { key: 'siteName', label: 'Site',  filter: true, type:AimsCommonTableHeaderType.STRING },
    { key: 'scheduleItemStart', label: 'Start', filter: true, type:AimsCommonTableHeaderType.STRING },
    { key: 'scheduleItemEnd', label: 'End', filter: true, type:AimsCommonTableHeaderType.STRING }
  ];

  travelerLookupColumns:AimsCommonTableHeader[]  = [
    { key: 'description', label: 'Description', type: AimsCommonTableHeaderType.STRING },
    { key: 'revision', label: 'Revision', type: AimsCommonTableHeaderType.STRING },
    { key: 'status', label: 'Status', type: AimsCommonTableHeaderType.STRING },
  ];

  userLookupColumns: AimsCommonTableHeader[] = [
    { key: 'fullName', label: 'Name', type: AimsCommonTableHeaderType.STRING }
  ];

  //Spinner
  spinnerText: string = "Refreshing...";
  showSpinner: boolean = false;

  public formGroup: any;
  public cart: Cart;
  public inventoryAttachedToTypes: InvAttachedToTypes[];
  private currentUser;
  private transferHttp: TransferHttpService;
  private workOrderValues: WoWorkOrders[];
  workOrderFilteredValues: BehaviorSubject<WoWorkOrders[]> = new BehaviorSubject([]);
  equipmentList: Observable<any>;
  scheduleItems: Observable<any>;
  travelers: Observable<any>;
  employees: Observable<any>;

  public woShow: boolean;
  public assetShow: boolean;
  public scheduleShow: boolean;
  public travelerShow: boolean;
  public manualShow: boolean;

  public showWoLookup: boolean = true;
  public showWoCreate: boolean;
  woOnly: boolean;
  tenantConfig: TenantConfig;
  inventoryTenantConfig : InventoryConfig;

  constructor(contentData: AimsCommonPopupEditorData) {
    this.formGroup = contentData.formGroup;
    this.cart = contentData.contentData.cart;
    this.inventoryAttachedToTypes = contentData.contentData.inventoryAttachedToTypes;
    this.transferHttp = contentData.contentData.transferHttp;

    //Changed this to pre-load in parent component
    this.workOrderValues = contentData.contentData.workOrders;
    this.workOrderFilteredValues.next(contentData.contentData.workOrders);

    this.tenantConfig = this.transferHttp.tenantConfig;
    this.inventoryTenantConfig = this.tenantConfig.inventoryConfig;

    this.formGroup.addControl('cart', new FormControl(this.cart));
    this.formGroup.addControl('attachedToType', new FormControl(null, [Validators.required]));

    this.formGroup.addControl('workOrderId', new FormControl(null));
    this.formGroup.addControl('equipmentId', new FormControl(null));
    this.formGroup.addControl('scheduleItemId', new FormControl(null));
    this.formGroup.addControl('travelerId', new FormControl(null));
    this.formGroup.addControl('manualNote', new FormControl(''));
    this.formGroup.addControl('pulledForId', new FormControl('', [Validators.required]));

    this.formGroup.addControl('woAction', new FormControl('lookup'));
    this.formGroup.addControl('workOrderNumber', new FormControl());
    this.formGroup.addControl('woCreate', new FormControl());
  }

  ngOnInit(): void {
    // temp site to get things rolling
    this.currentUser = this.transferHttp.getCache('user');
    let siteId = this.currentUser.defaultSiteId;
    if (this.cart.cartItems.length > 0) {
      let cartItem: CartItem = this.cart.cartItems[0];
      siteId = cartItem.inventoryItem.siteId;
      this.workOrderFilteredValues.next(this.workOrderValues.filter(w => w.siteId = siteId));
    }

    // maybe get these when type selected?
    this.equipmentList = this.transferHttp.getSingle<Array<FacEquipment>>('Common/GetEquipment');
    this.scheduleItems = this.transferHttp.getSingle('Sch/GetScheduleItemsPendingAndApproved');
    this.travelers = this.transferHttp.getHot('Inventory/GetTravelers');
    this.employees = this.transferHttp.getCache(CacheItem.allEmployees);

    this.formGroup.patchValue({
      pulledForId :  this.currentUser.employeeId,
    }, {onlySelf:true} );

    if (!this.inventoryTenantConfig.allowInventoryToCreateWorkOrders) {
      this.showWoLookup = true;
    }

    if (this.cart.cartItems.find(ci => ci.inventoryItem.workOrderRequired) != null) {
      this.woOnly = true;

      this.formGroup.patchValue({
        attachedToType:  1,
      }, {onlySelf:true} );
      this.formGroup.controls.attachedToType.disable();
      this.woShow = true;
    }

    this.checkNumbers();
  }

  checkNumbers() {
    this.formGroup.controls.cart.setErrors(null);
    this.cart.cartItems.forEach(i => {
      if (i.quantity > i.instock) {
        this.formGroup.controls.cart.setErrors({'incorrect': true});
      }
    });
  }

  handleRowEdit(update: TableRowUpdate) {
    let x = update.oldData;

  }

  public handleRowDelete(deleted: TableRowDelete) {
    this.cart.cartItems.splice(deleted.index, 1);
    this.cart.cartItems= [...this.cart.cartItems];

    this.formGroup.patchValue({
      cart:  this.cart,
    }, {onlySelf:true} );

    this.formGroup.markAsDirty();
  }

  public handleRowUpdate(update: TableRowUpdate): void {
    const updatedLineItem: CartItem = update.newRow as CartItem;

    this.cart.cartItems[update.index] = updatedLineItem;
    this.cart.cartItems = [...this.cart.cartItems];

    this.formGroup.patchValue({
      cart:  this.cart,
    }, {onlySelf:true} );

    this.formGroup.markAsDirty();
    this.checkNumbers();
  }

  attachedToChange(value) {
    this.woShow = false;
    this.assetShow = false;
    this.scheduleShow = false;
    this.travelerShow = false;
    this.manualShow = false;

    switch(value) {
      case 1 :
        this.woShow = true;
        break;
      case 2 :
        this.assetShow = true;
        break;
      case 3 :
        this.scheduleShow = true;
        break;
      case 4 :
        this.travelerShow = true;
        break;
      case 5 :
        this.manualShow = true;
        break;
    }

    this.woRequired();
    this.assetRequired();
    this.scheduleRequired();
    this.travelerRequired();
    this.manualRequired();
  }

  woRequired() {
    this.formGroup.controls.workOrderId.setValidators((this.woShow && this.showWoLookup) ? [Validators.required] : null);
    this.formGroup.controls.workOrderId.updateValueAndValidity();

    this.formGroup.controls.workOrderNumber.setValidators((this.woShow && this.showWoCreate) ? [Validators.required] : null);
    this.formGroup.controls.workOrderNumber.updateValueAndValidity();
  }

  assetRequired() {
    this.formGroup.controls.equipmentId.setValidators(this.assetShow ? [Validators.required] : null);
    this.formGroup.controls.equipmentId.updateValueAndValidity();
  }

  scheduleRequired() {
    this.formGroup.controls.scheduleItemId.setValidators(this.scheduleShow ? [Validators.required] : null);
    this.formGroup.controls.scheduleItemId.updateValueAndValidity();
  }

  travelerRequired() {
    this.formGroup.controls.travelerId.setValidators(this.travelerShow ? [Validators.required] : null);
    this.formGroup.controls.travelerId.updateValueAndValidity();
  }

  manualRequired() {
    this.formGroup.controls.manualNote.setValidators(this.manualShow ? [Validators.required] : null);
    this.formGroup.controls.manualNote.updateValueAndValidity();
  }

  public woActionChange(event) {
    if (event.value == 'lookup') {
      this.showWoLookup = true;
      this.showWoCreate = false;
    }
    else {
      this.showWoLookup = false;
      this.showWoCreate = true;
    }

    this.formGroup.patchValue({
      woAction:  event.value,
    }, {onlySelf:true} );

    this.woRequired();
  }

  updateInstock() {
    this.showSpinner = true
    let ids: number[] = [];
    this.cart.cartItems.forEach(ci => {
      ids.push(ci.inventoryItem.inventoryItemId);
    });

    this.transferHttp.put('inventory/GetInventoryItemsById', ids).subscribe({
      next: items => {
        this.cart.cartItems.forEach(ci => {
          let stockUpdate = items.find(s => s.inventoryItemId === ci.inventoryItem.inventoryItemId);
          ci.instock = stockUpdate.quantity;
        });
        this.showSpinner = false;
      },
      error: err => {
        this.showSpinner = false;
      }
    });
  }

}
